import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type Locale = 'es' | 'en';
type Bundle = Record<string, string>;

/**
 * Servicio i18n minimalista basado en Signals + JSON estático.
 *
 * Carga `assets/i18n/{locale}.json` y expone `t(key)` reactivo. La elección de
 * NO usar `@angular/localize` runtime / ngx-translate / transloco se justifica
 * porque el portal tiene 2 idiomas (ES/EN) y la complejidad extra no aporta:
 * con Signals se logra reactividad para cambios en vivo desde Settings.
 *
 * Patrón de uso (template):
 *   <span>{{ i18n.t('menu.tracking') }}</span>
 */
@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly http = inject(HttpClient);
  private readonly _locale = signal<Locale>(this.detectInitial());
  private readonly _bundle = signal<Bundle>({});

  readonly locale = this._locale.asReadonly();
  readonly ready = computed(() => Object.keys(this._bundle()).length > 0);

  async load(locale: Locale = this._locale()): Promise<void> {
    const bundle = await firstValueFrom(this.http.get<Bundle>(`assets/i18n/${locale}.json`));
    this._locale.set(locale);
    this._bundle.set(bundle);
    document.documentElement.lang = locale;
    localStorage.setItem('fuga.locale', locale);
  }

  t(key: string, params: Record<string, string | number> = {}): string {
    const tpl = this._bundle()[key] ?? key;
    return Object.keys(params).reduce((acc, p) => acc.replaceAll(`{${p}}`, String(params[p])), tpl);
  }

  private detectInitial(): Locale {
    const stored = localStorage.getItem('fuga.locale') as Locale | null;
    if (stored === 'es' || stored === 'en') return stored;
    return navigator.language?.startsWith('en') ? 'en' : 'es';
  }
}
